import { AssetsPhotoActions, AssetsPhotoStubActionable, processPhoto } from '@/services/assets-api/AssetsPhotoService';
import { AssetsDocumentActions, AssetsDocumentStub, processDocument } from '@/services/assets-api/AssetsDocumentService';
import { getDefaultAssets } from '@/_legacy/services/checklist';

interface FormItem {
  params: AssetsPhotoStubActionable | Record<string, unknown>;
  items: Array<FormItem>;
}

interface FormWithImage {
  image: string;
  action?: AssetsPhotoActions;
  image_id?: string;
  items: Array<FormWithImage>;
}

interface FormWithDocument {
  action?: AssetsDocumentActions;
  file: File;
  file_id?: string;
  file_name?: string;
  file_type?: string;
  items: Array<FormWithDocument>;
  assets: Assets;
}

interface Assets {
  documents: AssetOptions;
  images: AssetOptions;
  videos: AssetOptions;
  audios: AssetOptions;
}

type AssetOptions = AssetOption[];

interface AssetOption {
  id: string;
  fileName: string;
}

async function processFormItemsParamsPhotos(item: FormItem, filesAcc: string[]) {
  if (item.params && item.params.action) {
    if (item.params.image && !item.params.image_id) {
      item.params.action = AssetsPhotoActions.ADD_IMAGE;
    }

    const processedPhoto = await processPhoto(item.params as AssetsPhotoStubActionable);

    if (processedPhoto?.image_id) {
      filesAcc.push(processedPhoto?.image_id);
    }

    item.params = processedPhoto || {};
  }

  if (item.items && item.items.length > 0) {
    const promises = item.items.map(formItem => processFormItemsParamsPhotos(formItem, filesAcc));
    await Promise.all(promises);
  }
}

async function processFormQuestionImages(item: FormWithImage, filesAcc: string[]) {
  if (item.image) {
    if (item.image && !item.image_id) {
      item.action = AssetsPhotoActions.ADD_IMAGE_TO_QUESTION;
    }

    const processedPhoto = await processPhoto(item as unknown as AssetsPhotoStubActionable);

    if (processedPhoto?.image_id) {
      filesAcc.push(processedPhoto?.image_id);
    }

    item.image_id = processedPhoto?.image_id as (string | undefined);
  }

  if (item.items && item.items.length > 0) {
    const promises = item.items.map(formItem => processFormQuestionImages(formItem, filesAcc));
    await Promise.all(promises);
  }
}

async function processFormQuestionDocuments(item: FormWithDocument, filesAcc: string[]) {
  if (item.file) {
    if (item.file && !item.file_id) {
      item.action = AssetsDocumentActions.ADD_DOCUMENT;
    }

    const processedFile = await processDocument(item as unknown as AssetsDocumentStub);

    if (processedFile?.file_name) {
      filesAcc.push(processedFile?.file_name);
    }

    if (processedFile.file_id) {
      if (!item.assets) {
        item.assets = getDefaultAssets();
      }
      if (item.assets[processedFile.file_type]) {
        item.assets[processedFile.file_type].push({
          id: processedFile.file_id,
          fileName: processedFile.file_name,
        });
      } else {
        item.assets[processedFile.file_type] = [{
          id: processedFile.file_id,
          fileName: processedFile.file_name,
        }];
      }
      delete item.file_id;
      delete item.file_name;
      delete item.file_type;
    }
  }

  if (item.file_id) {
    item.action = AssetsDocumentActions.DELETE_DOCUMENT;
    await processDocument(item as unknown as AssetsDocumentStub);
    delete item.file_id;
  }

  if (item.items && item.items.length > 0) {
    const promises = item.items.map(formItem => processFormQuestionDocuments(formItem, filesAcc));
    await Promise.all(promises);
  }
}

interface ProcessPhotosResponse {
  checklistRecord: Record<string, unknown>;
  files: string[];
}

export const processPhotos = async (checklistRecord: Record<string, unknown>): Promise<ProcessPhotosResponse> => {
  const paramPhotos: string[] = [];
  const questionImages: string[] = [];
  const questionDocuments: string[] = [];

  await processFormItemsParamsPhotos(checklistRecord.form as FormItem, paramPhotos);
  await processFormQuestionImages(checklistRecord.form as FormWithImage, questionImages);
  await processFormQuestionDocuments(checklistRecord.form as FormWithDocument, questionDocuments);

  return {
    checklistRecord,
    files: [...paramPhotos, ...questionImages, ...questionDocuments],
  };
};
